import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, useAuth } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { Video, Clock, Plus, Pencil, Trash2, Users, X, MessageSquare, CheckCircle2, UserX } from "lucide-react";
import { toast } from "sonner";

const fmtTime = (t) => {
  const [h, m] = (t || "").split(":").map(Number);
  if (isNaN(h)) return t;
  const ap = h >= 12 ? "PM" : "AM";
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${ap} IST`;
};

function ClassDialog({ initial, onClose, onSaved }) {
  const { user } = useAuth();
  const { pick } = useLang();
  const [tracks, setTracks] = useState([]);
  const [form, setForm] = useState(initial || { course: "", instructor: user.name, date: "", time: "", duration: "60 min", track_id: "", repeat_weekly: false, weeks: 8 });
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/tracks").then((r) => setTracks(r.data)).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { ...form, track_id: form.track_id || null };
      if (initial?.id) {
        await api.put(`/live-classes/${initial.id}`, body);
        toast.success("Class updated");
      } else {
        const { data } = await api.post("/live-classes", body);
        toast.success(data.created_count ? `Weekly series created — ${data.created_count} classes scheduled` : "Live class scheduled");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    } finally {
      setBusy(false);
    }
  };
  const input = "w-full h-11 px-3.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-teal-600";
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center px-4" onClick={onClose}>
      <div data-testid="class-dialog" className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="font-heading font-extrabold text-slate-900">{initial?.id ? "Edit live class" : "Schedule a live class"}</h2>
          <button data-testid="class-dialog-close" onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <input data-testid="class-course-input" required placeholder="Class title (e.g., Refraction Basics — Live Q&A)" value={form.course}
            onChange={(e) => setForm({ ...form, course: e.target.value })} className={input} />
          <input data-testid="class-instructor-input" required placeholder="Instructor name" value={form.instructor}
            onChange={(e) => setForm({ ...form, instructor: e.target.value })} className={input} />
          <div className="grid grid-cols-2 gap-3">
            <input data-testid="class-date-input" required type="date" value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })} className={input} />
            <input data-testid="class-time-input" required type="time" value={form.time}
              onChange={(e) => setForm({ ...form, time: e.target.value })} className={input} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <select data-testid="class-duration-select" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} className={`${input} bg-white`}>
              {["30 min", "45 min", "60 min", "90 min"].map((d) => <option key={d}>{d}</option>)}
            </select>
            <select data-testid="class-track-select" value={form.track_id || ""} onChange={(e) => setForm({ ...form, track_id: e.target.value })} className={`${input} bg-white`}>
              <option value="">No linked course</option>
              {tracks.map((t) => <option key={t.id} value={t.id}>{pick(t.title)}</option>)}
            </select>
          </div>
          <p className="text-[11px] text-slate-400">Linking a course enables attendance tracking against its enrolled students.</p>
          {!initial?.id && (
            <div className="flex items-center gap-3 bg-teal-50/60 border border-teal-100 rounded-xl px-3.5 py-2.5">
              <input data-testid="class-repeat-checkbox" id="repeat-weekly" type="checkbox" checked={form.repeat_weekly}
                onChange={(e) => setForm({ ...form, repeat_weekly: e.target.checked })} className="w-4 h-4 accent-teal-700" />
              <label htmlFor="repeat-weekly" className="text-sm text-slate-700 font-medium flex-1">Repeat weekly</label>
              {form.repeat_weekly && (
                <select data-testid="class-weeks-select" value={form.weeks} onChange={(e) => setForm({ ...form, weeks: Number(e.target.value) })}
                  className="h-9 px-2 rounded-lg border border-slate-200 text-sm bg-white">
                  {[4, 6, 8, 10, 12].map((w) => <option key={w} value={w}>{w} weeks</option>)}
                </select>
              )}
            </div>
          )}
          <button data-testid="class-save-btn" type="submit" disabled={busy}
            className="w-full h-11 rounded-xl bg-teal-800 hover:bg-teal-700 text-white font-semibold text-sm disabled:opacity-50 transition-colors">
            {busy ? "Saving…" : initial?.id ? "Save changes" : "Schedule class"}
          </button>
        </form>
      </div>
    </div>
  );
}

function AttendanceDialog({ cls, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/live-classes/${cls.id}/attendance`).then((r) => setData(r.data)).catch(() => setData({ attendees: [], absentees: [] })); }, [cls.id]);
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center px-4" onClick={onClose}>
      <div data-testid="attendance-dialog" className="w-full max-w-lg bg-white rounded-2xl shadow-2xl p-6 space-y-4 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-heading font-extrabold text-slate-900">Attendance</h2>
            <p className="text-xs text-slate-500">{cls.course} · {new Date(cls.date).toLocaleDateString(undefined, { month: "long", day: "numeric" })} at {fmtTime(cls.time)}</p>
          </div>
          <button data-testid="attendance-close-btn" onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
        </div>
        {!data ? <p className="text-sm text-slate-400">Loading…</p> : (
          <>
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-emerald-700 mb-2 flex items-center gap-1.5"><CheckCircle2 size={13} /> Joined ({data.attendees.length})</p>
              {data.attendees.length === 0 ? <p className="text-sm text-slate-400">Nobody has joined yet.</p> : (
                <div className="border border-slate-100 rounded-xl divide-y divide-slate-50">
                  {data.attendees.map((a) => (
                    <div key={a.user_id} data-testid={`attendee-${a.user_id}`} className="px-3.5 py-2 flex items-center justify-between text-sm">
                      <span className="text-slate-700 font-medium">{a.name}</span>
                      <span className="text-[11px] text-slate-400">{a.role === "trainee" ? "Student" : a.role} · {new Date(a.joined_at).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {cls.track_id != null && (
              <div>
                <p className="text-[11px] font-bold uppercase tracking-wider text-amber-700 mb-2 flex items-center gap-1.5"><UserX size={13} /> Enrolled but absent ({data.absentees.length})</p>
                {data.absentees.length === 0 ? <p className="text-sm text-slate-400">Full attendance — every enrolled student joined 🎉</p> : (
                  <div className="border border-slate-100 rounded-xl divide-y divide-slate-50">
                    {data.absentees.map((a) => (
                      <div key={a.id} data-testid={`absentee-${a.id}`} className="px-3.5 py-2 flex items-center justify-between text-sm gap-2">
                        <span className="min-w-0"><span className="block text-slate-700 font-medium truncate">{a.name}</span><span className="block text-[11px] text-slate-400 truncate">{a.email}</span></span>
                        <Link data-testid={`follow-up-${a.id}`} to={`/messages?to=${a.id}`}
                          className="shrink-0 h-8 px-3 rounded-lg border border-teal-700 text-teal-800 text-xs font-semibold inline-flex items-center gap-1 hover:bg-teal-50">
                          <MessageSquare size={12} /> Follow up
                        </Link>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default function LiveClasses() {
  const { user } = useAuth();
  const [classes, setClasses] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [attendance, setAttendance] = useState(null);
  const [delTarget, setDelTarget] = useState(null);
  const navigate = useNavigate();
  const isStaff = ["assessor", "admin", "superadmin"].includes(user?.role);
  const load = () => api.get("/live-classes").then((r) => setClasses(r.data)).catch(() => setClasses([]));
  useEffect(() => { load(); }, []);

  const join = async (c) => {
    try {
      const { data } = await api.post(`/live-classes/${c.id}/join`);
      navigate(`/call/${data.room}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not join");
    }
  };

  const remove = async (c, scope = "one") => {
    try {
      const { data } = await api.delete(`/live-classes/${c.id}?scope=${scope}`);
      toast.success(data.series_deleted ? `Series removed (${data.series_deleted} classes)` : "Class removed");
      setDelTarget(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not delete");
    }
  };

  if (!classes) return <p className="text-slate-500">Loading…</p>;
  return (
    <div className="space-y-4">
      {dialog && <ClassDialog initial={dialog.id ? dialog : null} onClose={() => setDialog(null)} onSaved={load} />}
      {attendance && <AttendanceDialog cls={attendance} onClose={() => setAttendance(null)} />}
      {delTarget && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center px-4" onClick={() => setDelTarget(null)}>
          <div data-testid="delete-series-dialog" className="w-full max-w-sm bg-white rounded-2xl shadow-2xl p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-heading font-extrabold text-slate-900">Delete recurring class</h2>
            <p className="text-sm text-slate-500">"{delTarget.course}" is part of a weekly series. What would you like to remove?</p>
            <div className="flex flex-col gap-2">
              <button data-testid="delete-one-btn" onClick={() => remove(delTarget, "one")}
                className="h-11 rounded-xl border border-slate-300 text-slate-700 text-sm font-semibold hover:bg-slate-50">Only this class</button>
              <button data-testid="delete-series-btn" onClick={() => remove(delTarget, "series")}
                className="h-11 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-semibold">Entire series</button>
              <button data-testid="delete-cancel-btn" onClick={() => setDelTarget(null)} className="text-xs text-slate-400 hover:text-slate-600 py-1">Cancel</button>
            </div>
          </div>
        </div>
      )}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-slate-900">Live Classes</h1>
          <p className="text-sm text-slate-500">Join right inside the app with built-in video — attendance is recorded automatically.</p>
        </div>
        {isStaff && (
          <button data-testid="add-class-btn" onClick={() => setDialog({})}
            className="h-11 px-4 rounded-xl bg-teal-800 hover:bg-teal-700 text-white text-sm font-semibold inline-flex items-center gap-1.5 transition-colors">
            <Plus size={15} /> Add live class
          </button>
        )}
      </div>
      {classes.length === 0 && <p className="text-sm text-slate-400 bg-white rounded-2xl border border-slate-200 p-6 text-center">No live classes scheduled yet.</p>}
      {classes.map((c) => (
        <div key={c.id} data-testid={`live-class-${c.id}`} className="bg-white rounded-2xl border border-slate-200 p-5 flex flex-col sm:flex-row sm:items-center gap-4">
          <span className="w-12 h-12 rounded-xl bg-teal-50 text-teal-700 flex items-center justify-center shrink-0"><Video size={22} /></span>
          <div className="flex-1">
            <h2 className="font-heading font-bold text-slate-900">{c.course}</h2>
            <p className="text-sm text-slate-500">{c.instructor}</p>
            {c.scheduled_with && <span data-testid={`session-badge-${c.id}`} className="inline-block mt-1 text-[10px] font-bold uppercase tracking-wide text-teal-700 bg-teal-50 px-2 py-0.5 rounded-full">1-on-1 session with {c.scheduled_with}</span>}
            {c.series_id && <span data-testid={`series-badge-${c.id}`} className="inline-block mt-1 ml-1 text-[10px] font-bold uppercase tracking-wide text-violet-700 bg-violet-50 px-2 py-0.5 rounded-full">Weekly series</span>}
            <p className="text-sm text-slate-500 flex items-center gap-1.5 mt-1"><Clock size={14} /> {new Date(c.date).toLocaleDateString(undefined, { month: "long", day: "numeric" })} · {fmtTime(c.time)} · {c.duration}</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button data-testid={`join-class-${c.id}`} onClick={() => join(c)}
              className="h-11 px-5 rounded-xl bg-teal-700 text-white font-semibold text-sm hover:bg-teal-800">Join Live Class</button>
            {isStaff && (
              <button data-testid={`attendance-btn-${c.id}`} onClick={() => setAttendance(c)} title="Attendance"
                className="h-11 px-3.5 rounded-xl border border-slate-300 text-slate-600 hover:text-teal-700 hover:border-teal-300 flex items-center"><Users size={17} /></button>
            )}
            {isStaff && !c.personal && (
              <>
                <button data-testid={`edit-class-${c.id}`} onClick={() => setDialog(c)} title="Edit"
                  className="h-11 px-3.5 rounded-xl border border-slate-300 text-slate-600 hover:text-teal-700 hover:border-teal-300 flex items-center"><Pencil size={16} /></button>
                <button data-testid={`delete-class-${c.id}`} onClick={() => (c.series_id ? setDelTarget(c) : remove(c))} title="Delete"
                  className="h-11 px-3.5 rounded-xl border border-slate-300 text-slate-600 hover:text-red-600 hover:border-red-300 flex items-center"><Trash2 size={16} /></button>
              </>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
